/** Viewport (canvas) size — 16:9 game logic space. */
export const BOAT_SHARK_CROSSING_LOGICAL_WIDTH = 640;
export const BOAT_SHARK_CROSSING_LOGICAL_HEIGHT = 360;

/** Scrollable world width (camera pans east across the crossing). */
export const BOAT_SHARK_CROSSING_WORLD_WIDTH = 3200;

export const BOAT_SHARK_CROSSING_ROCKS_SRC = '/minigames/rocks.png';
export const BOAT_SHARK_CROSSING_BOAT_SRC = '/minigames/boat.png';
export const BOAT_SHARK_CROSSING_SHARK_SRC = '/minigames/shark.png';
export const BOAT_SHARK_CROSSING_DIE_SOUND_SRC = '/minigames/die.mp3';

/** Server `minigames.boatSharkCrossing.winPickValue` */
export const BOAT_SHARK_CROSSING_WIN_PICK_VALUE = 1;
/** Server `minigames.boatSharkCrossing.lossPickValue` */
export const BOAT_SHARK_CROSSING_LOSS_PICK_VALUE = 0;
/** Server `minigames.boatSharkCrossing.maxLives` */
export const BOAT_SHARK_CROSSING_MAX_LIVES = 3;

/** Server `minigames.boatSharkCrossing.sharkSpawnIntervalMs` */
export const BOAT_SHARK_CROSSING_SHARK_SPAWN_INTERVAL_MS = 475;
/** Server `minigames.boatSharkCrossing.sharksPerSpawn` */
export const BOAT_SHARK_CROSSING_SHARKS_PER_SPAWN = 2;
/** Server `minigames.boatSharkCrossing.sharkSpeedMin` */
export const BOAT_SHARK_CROSSING_SHARK_SPEED_MIN = 3.8;
/** Server `minigames.boatSharkCrossing.sharkSpeedMax` */
export const BOAT_SHARK_CROSSING_SHARK_SPEED_MAX = 6.5;
/** Server `minigames.boatSharkCrossing.maxActiveSharks` */
export const BOAT_SHARK_CROSSING_MAX_ACTIVE_SHARKS = 64;

export const BOAT_SHARK_CROSSING_BOAT_WIDTH = 56;
export const BOAT_SHARK_CROSSING_BOAT_HEIGHT = 36;
export const BOAT_SHARK_CROSSING_SHARK_WIDTH = 48;
export const BOAT_SHARK_CROSSING_SHARK_HEIGHT = 30;
export const BOAT_SHARK_CROSSING_ROCK_WIDTH = 52;
export const BOAT_SHARK_CROSSING_ROCK_HEIGHT = 40;

export const BOAT_SHARK_CROSSING_PLAYER_SPEED = 3.6;
export const BOAT_SHARK_CROSSING_PLAY_MARGIN_Y = 28;
export const BOAT_SHARK_CROSSING_GOAL_X = BOAT_SHARK_CROSSING_WORLD_WIDTH - 52;

/** Static rock obstacles placed across the crossing (world coordinates). */
export const BOAT_SHARK_CROSSING_ROCKS = Object.freeze([
  { x: 380, y: 72, w: 52, h: 40 },
  { x: 520, y: 248, w: 48, h: 38 },
  { x: 700, y: 140, w: 54, h: 42 },
  { x: 860, y: 56, w: 50, h: 40 },
  { x: 980, y: 260, w: 52, h: 40 },
  { x: 1180, y: 100, w: 48, h: 38 },
  { x: 1320, y: 220, w: 54, h: 42 },
  { x: 1520, y: 68, w: 50, h: 40 },
  { x: 1680, y: 190, w: 52, h: 40 },
  { x: 1880, y: 110, w: 48, h: 38 },
  { x: 2040, y: 252, w: 52, h: 40 },
  { x: 2240, y: 84, w: 50, h: 40 },
  { x: 2460, y: 200, w: 54, h: 42 },
  { x: 2680, y: 130, w: 48, h: 38 },
  { x: 2880, y: 240, w: 52, h: 40 },
]);

/**
 * @param {number} boatCenterX
 * @returns {number}
 */
export function computeBoatSharkCameraX(boatCenterX) {
  const halfView = BOAT_SHARK_CROSSING_LOGICAL_WIDTH / 2;
  const maxCam = BOAT_SHARK_CROSSING_WORLD_WIDTH - BOAT_SHARK_CROSSING_LOGICAL_WIDTH;
  return Math.max(0, Math.min(maxCam, boatCenterX - halfView));
}

/**
 * @typedef {{ x: number, y: number, w: number, h: number }} BoatSharkRect
 * @typedef {{ x: number, y: number, w: number, h: number, vx: number }} BoatSharkEnemy
 */

/** @type {BoatSharkRect} */
export const BOAT_SHARK_CROSSING_BOAT_SPAWN = Object.freeze({
  x: 28,
  y: (BOAT_SHARK_CROSSING_LOGICAL_HEIGHT - BOAT_SHARK_CROSSING_BOAT_HEIGHT) / 2,
  w: BOAT_SHARK_CROSSING_BOAT_WIDTH,
  h: BOAT_SHARK_CROSSING_BOAT_HEIGHT,
});

/**
 * @param {BoatSharkRect} a
 * @param {BoatSharkRect} b
 */
export function boatSharkRectsOverlap(a, b) {
  const inset = 4;
  return (
    a.x + inset < b.x + b.w - inset &&
    a.x + a.w - inset > b.x + inset &&
    a.y + inset < b.y + b.h - inset &&
    a.y + a.h - inset > b.y + inset
  );
}

/**
 * @param {BoatSharkRect} boat
 * @param {readonly BoatSharkRect[]} rocks
 */
export function boatOverlapsAnyRock(boat, rocks) {
  for (const rock of rocks) {
    if (boatSharkRectsOverlap(boat, rock)) {
      return true;
    }
  }
  return false;
}

/**
 * @param {BoatSharkRect} boat
 * @param {number} dx
 * @param {number} dy
 * @param {readonly BoatSharkRect[]} rocks
 * @param {{ xMin: number, xMax: number, yMin: number, yMax: number }} bounds
 */
export function moveBoatWithRockBlocking(boat, dx, dy, rocks, bounds) {
  if (dx !== 0) {
    const nextX = Math.max(bounds.xMin, Math.min(bounds.xMax, boat.x + dx));
    const trial = { x: nextX, y: boat.y, w: boat.w, h: boat.h };
    if (!boatOverlapsAnyRock(trial, rocks)) {
      boat.x = nextX;
    }
  }
  if (dy !== 0) {
    const nextY = Math.max(bounds.yMin, Math.min(bounds.yMax, boat.y + dy));
    const trial = { x: boat.x, y: nextY, w: boat.w, h: boat.h };
    if (!boatOverlapsAnyRock(trial, rocks)) {
      boat.y = nextY;
    }
  }
}

/**
 * Moves a shark horizontally and blocks it at rock obstacles.
 * Returns true when the shark can remain active after moving.
 *
 * @param {BoatSharkEnemy} shark
 * @param {number} deltaX
 * @param {readonly BoatSharkRect[]} rocks
 * @returns {boolean}
 */
export function moveSharkWithRockBlocking(shark, deltaX, rocks) {
  if (deltaX === 0) {
    return true;
  }
  const trial = { x: shark.x + deltaX, y: shark.y, w: shark.w, h: shark.h };
  if (boatOverlapsAnyRock(trial, rocks)) {
    return false;
  }
  shark.x = trial.x;
  return true;
}

/**
 * @param {number} min
 * @param {number} max
 */
export function boatSharkRandomBetween(min, max) {
  return min + Math.random() * (max - min);
}
